import {
  IcCalculator,
  IcPackageOpen,
  IcMoreHorizontal,
  IcHistory,
  IcCalendarCheck,
  IcClipboardList,
  IcSparkles,
  IcTruck,
  IcFlask,
  IcTags,
  IcShoppingBag,
  IcSettings,
  IcBanknote,
  IcUsers,
  IcReceiptText,
  IcLineChart,
  IcBarChart,
  IcFileText,
} from '../../theme/icons';

/**
 * Role-aware bottom tab bar (max 5 items — native convention).
 * The last tab (`more`) opens the slide-up sheet with everything else.
 */
export function getPrimaryTabs(user) {
  const role = user?.role;
  const isAdmin = role === 'admin';

  if (role === 'seller' || role === 'staff') {
    return [
      { to: '/seller-sales', label: 'Vendre', icon: IcCalculator },
      { to: '/seller-history', label: 'Mes ventes', icon: IcHistory },
      { to: '/products', label: 'Produits', icon: IcPackageOpen },
      { to: '/services', label: 'Services', icon: IcSparkles },
      { more: true, label: 'Plus', icon: IcMoreHorizontal },
    ];
  }

  // admin + manager — owner Studio 2026-08-14 : Paiement (popup Type de
  // paiement), Achat (popup Type d'achat), Ventes (centre, inchangé),
  // Réservations (page /reservations, mode Soin par défaut), Produits.
  return [
    { action: 'paiement', label: 'Paiement', icon: IcBanknote },
    { action: 'achat', label: 'Achat', icon: IcShoppingBag },
    // Raised center button → the POS ("Point de vente").
    {
      to: '/seller-sales', label: 'Ventes', icon: IcCalculator, center: true,
      activeMatch: (loc) => loc.pathname.startsWith('/seller-sales') && !loc.state?.openSoin,
    },
    { to: '/reservations', label: 'Réservations', icon: IcCalendarCheck },
    { to: '/products', label: 'Produits', icon: IcPackageOpen },
  ];
}

/**
 * The More sheet's shortcut list.
 * Réorganisé par fréquence d'usage avec des sections (Ahmed 2026-08-16) ;
 * les vendeurs gardent l'ancienne liste plate. « back » = revenir à
 * l'ancienne liste plate de 7 raccourcis pour tous.
 */
export function getMoreLinks(user) {
  const role = user?.role;
  if (role === 'seller' || role === 'staff') {
    return [
      { to: '/products/promos', label: 'Promotion', icon: IcTags },
      { to: '/clients', label: 'Client', icon: IcUsers },
      { to: '/services', label: 'Liste de services', icon: IcSparkles },
      { to: '/commandes/a-commander', label: 'Nouvelle commande', icon: IcClipboardList },
      // Owner 2026-09-21 : le compte vendeur ajoute des factures d'achat.
      { to: '/facturation/add', label: 'Ajouter une facture', icon: IcReceiptText },
      { to: '/suppliers', label: 'Fournisseur', icon: IcTruck },
      { to: '/laboratoires', label: 'Laboratoire', icon: IcFlask },
      { to: '/settings', label: 'Paramètres', icon: IcSettings },
    ];
  }

  // Admin / manager — sélection Ahmed 2026-08-16 (outil de sélection sur le
  // site réel) : retrait de Nouvelle commande / Laboratoire / Liste de
  // services / Utilisateurs (accessibles via leurs pages parentes), ordre
  // réorganisé par fréquence d'usage.
  return [
    { to: '/commandes/brouillons', label: 'Bons de commande', icon: IcFileText },
    { to: '/facturation/list', label: 'Facturation', icon: IcReceiptText },
    { to: '/suppliers', label: 'Fournisseur', icon: IcTruck },
    { to: '/clients', label: 'Client', icon: IcUsers },
    { to: '/products/promos', label: 'Promotion', icon: IcTags },
    { to: '/seller-history', label: 'Mes ventes', icon: IcHistory },
    { to: '/devis', label: 'Devis', icon: IcFileText },
    { to: '/statistiques/ventes', label: 'Statistiques ventes', icon: IcLineChart },
    { to: '/statistiques/generale', label: 'Statistiques générales', icon: IcBarChart },
    { to: '/settings', label: 'Paramètres', icon: IcSettings },
  ];
}

// Longest-prefix-wins title lookup for the mobile header.
const TITLES = [
  [/^\/$/, 'Tableau de bord'],
  [/^\/vente\/\d+\/receipt/, 'Reçu'],
  [/^\/vente\/\d+/, 'Détail vente'],
  [/^\/seller-sales/, 'Point de vente'],
  [/^\/seller-history/, 'Mes ventes'],
  [/^\/service-history/, 'Historique services'],
  [/^\/reservations/, 'Réservations'],
  [/^\/devis/, 'Devis'],
  [/^\/products\/add/, 'Nouveau produit'],
  [/^\/products\/promos/, 'Promotions'],
  [/^\/products\/\d+\/edit/, 'Modifier produit'],
  [/^\/products/, 'Produits'],
  [/^\/categories/, 'Catégories'],
  [/^\/clients\/add/, 'Nouveau client'],
  [/^\/clients\/categories/, 'Catégories clients'],
  [/^\/clients\/\d+\/edit/, 'Modifier client'],
  [/^\/clients\/\d+/, 'Fiche client'],
  [/^\/clients/, 'Clients'],
  [/^\/suppliers\/add/, 'Nouveau fournisseur'],
  [/^\/suppliers\/\d+/, 'Fournisseur'],
  [/^\/suppliers/, 'Fournisseurs'],
  [/^\/laboratoires/, 'Laboratoires'],
  [/^\/commandes\/a-commander/, 'Nouvelle commande'],
  [/^\/commandes/, 'Bons de commande'],
  [/^\/facturation\/add/, 'Nouvelle facture'],
  [/^\/facturation\/edit/, 'Modifier facture'],
  [/^\/facturation\/detail/, 'Détail facture'],
  [/^\/facturation/, 'Facturation'],
  [/^\/services\/add/, 'Nouveau service'],
  [/^\/services\/edit/, 'Modifier service'],
  [/^\/services/, 'Services'],
  [/^\/users\/add/, 'Nouvel utilisateur'],
  [/^\/users\/edit/, 'Modifier utilisateur'],
  [/^\/users/, 'Utilisateurs'],
  [/^\/statistiques\/ventes/, 'Statistiques ventes'],
  [/^\/statistiques\/generale/, 'Statistiques'],
  [/^\/settings/, 'Paramètres'],
];

export function titleForPath(pathname) {
  const found = TITLES.find(([re]) => re.test(pathname));
  return found ? found[1] : 'Kipara';
}

// Bottom-bar root paths — the header hides its back button on these.
export const ROOT_PATHS = [
  '/', '/products', '/clients',
  '/seller-sales', '/seller-history', '/services',
  '/facturation/list',
];
